import styled, { createGlobalStyle } from 'styled-components';
import { motion } from 'framer-motion';
export * from './Timeline.styles';

export const GlobalStyle = createGlobalStyle`
  * {
    cursor: none !important;
  }
`;

export const AppContainer = styled.div`
  width: 100vw;
  height: 100vh;
  background: var(--bg-primary);
  color: var(--text-primary);
  overflow: hidden;
  position: relative;
`;

export const CanvasBackground = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 1;
  opacity: 0.8;

  @media (max-width: 768px) {
    opacity: 0.6;
  }
`;

export const MainContent = styled(motion.div)`
  position: relative;
  z-index: 2;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  scroll-snap-type: y mandatory;
  -webkit-overflow-scrolling: touch;

  @media (max-width: 768px) {
    scroll-snap-type: none;
  }
`;

export const Section = styled.section`
  min-height: 100vh;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  scroll-snap-align: start;
  padding: 2rem;
  position: relative;

  @media (max-width: 768px) {
    padding: 1rem;
    min-height: auto;
  }
`;

export const SectionContent = styled.div`
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  padding: 2rem;
  background: var(--card-bg);
  border-radius: 8px;
  border: 1px solid var(--card-border);
  backdrop-filter: blur(4px);
  transition: all 0.3s ease;

  @media (max-width: 768px) {
    padding: 1rem;
    margin: 0.5rem;
  }

  &:hover {
    background: var(--bg-secondary);
    border-color: var(--accent-color);
  }
`;

export const SectionTitle = styled.h2`
  color: var(--accent-color);
  font-size: 2.5rem;
  margin-bottom: 2rem;
  text-align: center;
  position: relative;
  
  @media (max-width: 768px) {
    font-size: 2rem;
    margin-bottom: 1.5rem;
  }
  
  &::after {
    content: '';
    position: absolute;
    bottom: -10px;
    left: 50%;
    transform: translateX(-50%);
    width: 60px;
    height: 3px;
    background: var(--accent-color);
    border-radius: 2px;
  }
`;

export const Card = styled(motion.div)`
  background: var(--card-bg);
  border: 1px solid var(--card-border);
  border-radius: 10px;
  padding: 1rem;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
  height: 100%;
  display: flex;
  flex-direction: column;

  @media (max-width: 768px) {
    padding: 0.875rem;
  }

  &.featured-project {
    background: var(--bg-secondary);
    border: 1px solid var(--accent-color);
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);

    .project-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      margin-bottom: 0.75rem;
      padding-bottom: 0.5rem;
      border-bottom: 1px solid var(--card-border);

      h3 {
        margin: 0;
        font-size: 1.2rem;
        color: var(--accent-color);
      }

      .project-tag {
        font-size: 0.75rem;
        padding: 0.25rem 0.5rem;
        background: var(--accent-color);
        color: var(--bg-primary);
        border-radius: 12px;
        font-weight: 500;
        text-transform: uppercase;
        letter-spacing: 0.3px;
      }
    }

    ul {
      flex: 1;
      margin: 0;
      padding: 0;
      list-style: none;
      
      li {
        margin: 0.35rem 0;
        padding-left: 1.25rem;
        position: relative;
        color: var(--text-secondary);
        font-size: 0.9rem;
        line-height: 1.4;
        
        &::before {
          content: '→';
          position: absolute;
          left: 0;
          color: var(--accent-color);
        }
      }
    }

    &:hover {
      transform: translateY(-3px);
      box-shadow: 0 6px 20px rgba(0, 0, 0, 0.12);
    }
  }

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(45deg, transparent, var(--accent-color), transparent);
    opacity: 0.1;
    transform: translateX(-100%);
    transition: transform 0.6s ease;
  }

  &:hover {
    transform: translateY(-5px);
    border-color: var(--accent-color);
    box-shadow: 0 0 20px var(--card-shadow);

    &::before {
      transform: translateX(100%);
    }
  }

  h3 {
    color: var(--accent-color);
    margin-bottom: 0.5rem;
    font-size: 1.2rem;

    @media (max-width: 768px) {
      font-size: 1.1rem;
    }
  }

  ul {
    list-style: none;
    padding: 0;
    
    li {
      margin: 0.25rem 0;
      padding-left: 1.25rem;
      position: relative;
      color: var(--text-secondary);
      font-size: 0.9rem;
      
      @media (max-width: 768px) {
        font-size: 0.85rem;
      }
      
      &::before {
        content: '→';
        position: absolute;
        left: 0;
        color: var(--accent-color);
      }
    }
  }
`;

export const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 1rem;
  margin-top: 1rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 0.75rem;
    margin-top: 0.75rem;
    &.leadership-awards-grid {
      grid-template-columns: 1fr;
    }
  }

  &.projects-grid {
    margin-bottom: 1.5rem;
  }

  &.skills-grid {
    margin-bottom: 1.25rem;
  }
`;

export const StoryText = styled(motion.p)`
  font-size: 1.2rem;
  color: var(--text-secondary);
  line-height: 1.8;
  margin: 2rem 0;
  text-align: center;
  max-width: 800px;
  margin-left: auto;
  margin-right: auto;
  position: relative;
  padding: 1rem;
  
  @media (max-width: 768px) {
    font-size: 1rem;
    line-height: 1.6;
    margin: 1.5rem 0;
    padding: 0.5rem;
  }
  
  &::before, &::after {
    content: '"';
    position: absolute;
    font-size: 4rem;
    color: var(--accent-color);
    opacity: 0.2;
  }
  
  &::before {
    left: -20px;
    top: -20px;
  }
  
  &::after {
    right: -20px;
    bottom: -40px;
  }
`;

export const CourseCarousel = styled.div`
  margin-top: 1.5rem;
  padding: 1rem;
  background: var(--card-bg);
  border: 1px solid var(--card-border);
  border-radius: 12px;
  width: 100%;
  overflow: hidden;

  & + & {
    margin-top: 1rem;
  }

  .header {
    display: flex;
    align-items: center;
    margin-bottom: 0.75rem;

    h3 {
      color: var(--accent-color);
      font-size: 1.1rem;
      font-weight: 600;
      margin: 0;
      white-space: nowrap;
    }

    .line {
      flex: 1;
      height: 1px;
      background: var(--accent-color);
      opacity: 0.3;
      margin-left: 1rem;
    }
  }

  .scroll-container {
    position: relative;
    overflow: hidden;
    padding: 0.5rem 0;

    &::after, &::before {
      content: '';
      position: absolute;
      top: 0;
      height: 100%;
      width: 60px;
      pointer-events: none;
      z-index: 2;
    }

    &::after {
      right: 0;
      background: linear-gradient(to left, var(--card-bg) 0%, transparent 100%);
    }

    &::before {
      left: 0;
      background: linear-gradient(to right, var(--card-bg) 0%, transparent 100%);
    }
  }
`;

export const CourseList = styled(motion.div)`
  display: inline-flex;
  gap: 2rem;
  padding: 0 3rem;

  .course-item {
    color: var(--text-secondary);
    font-size: 0.9rem;
    white-space: nowrap;
    padding: 0.5rem 1rem;
    background: var(--bg-secondary);
    border: 1px solid var(--card-border);
    border-radius: 6px;
    transition: all 0.2s ease;
    cursor: pointer;
    position: relative;

    &:not(:last-child)::after {
      content: '•';
      position: absolute;
      right: -1.25rem;
      color: var(--accent-color);
      opacity: 0.5;
    }

    &:hover {
      color: var(--accent-color);
      border-color: var(--accent-color);
      transform: translateY(-2px);
    }
  }
`;

export const ContactGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2rem;
  margin-top: 2rem;
`;

export const ContactCard = styled(motion.div)`
  background: var(--card-bg);
  border: 1px solid var(--card-border);
  border-radius: 12px;
  padding: 1.25rem;
  backdrop-filter: blur(10px);
  transition: all 0.3s ease;

  h3 {
    margin-bottom: 0.5rem;
  }

  p {
    margin-bottom: 0.75rem;
  }

  &:hover {
    transform: translateY(-5px);
    border-color: var(--accent-color);
    box-shadow: 0 5px 20px rgba(0, 0, 0, 0.1);
  }
`;

export const ContactPreview = styled.div`
  background: var(--bg-secondary);
  border: 1px solid var(--card-border);
  border-radius: 8px;
  padding: 0.75rem;
  margin-top: 0.75rem;

  .message-container {
    display: flex;
    gap: 0.75rem;
    align-items: stretch;
  }

  .fields {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
  }

  .field {
    display: flex;
    align-items: center;
    padding: 0.5rem 0.75rem;
    border: 1px solid var(--card-border);
    border-radius: 6px;
    background: var(--card-bg);
    transition: all 0.2s ease;
    height: 40px;

    &.message-field {
      height: 70px;
    }

    &:focus-within {
      border-color: var(--accent-color);
      box-shadow: 0 0 0 2px var(--accent-color-alpha);
    }

    input, textarea {
      width: 100%;
      background: none;
      border: none;
      color: var(--text-primary);
      font-size: 0.95rem;
      font-family: inherit;
      outline: none;

      &::placeholder {
        color: var(--text-secondary);
        opacity: 0.7;
      }
    }

    textarea {
      padding: 0;
      line-height: 1.4;
      height: 100%;
      resize: none;
    }
  }

  .send-button {
    width: 40px;
    border-radius: 6px;
    transition: all 0.3s ease;

    &:hover {
      transform: translateY(0) scale(1.1);
    }

    svg {
      width: 1rem;
      height: 1rem;
    }
  }
`;

export const SocialLinks = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  margin-top: 0.75rem;
`;

export const SocialButton = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  background: var(--bg-secondary);
  border: 1px solid var(--card-border);
  border-radius: 6px;
  color: var(--text-primary);
  text-decoration: none;
  transition: all 0.2s ease;
  font-size: 0.9rem;

  svg {
    width: 18px;
    height: 18px;
    flex-shrink: 0;
    display: block;
  }

  &:hover {
    background: var(--accent-color);
    color: var(--bg-primary);
    border-color: var(--accent-color);
    transform: translateY(-2px);
  }
`;

export const StatusBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  background: var(--accent-color);
  color: var(--bg-primary);
  border-radius: 20px;
  font-size: 0.85rem;
  margin-top: 1rem;

  &::before {
    content: '';
    width: 8px;
    height: 8px;
    background: var(--bg-primary);
    border-radius: 50%;
    animation: pulse 2s infinite;
  }

  @keyframes pulse {
    0% { opacity: 1; }
    50% { opacity: 0.5; }
    100% { opacity: 1; }
  }
`;

export const QuickLinks = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  margin-top: 1rem;

  a {
    color: var(--text-secondary);
    font-size: 0.9rem;
    text-decoration: none;
    padding: 0.4rem 0.8rem;
    border-radius: 6px;
    background: var(--bg-secondary);
    transition: all 0.2s ease;

    &:hover {
      color: var(--accent-color);
      background: var(--card-bg);
    }
  }
`;

export const MilestoneGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1.5rem;
  padding: 0.5rem;
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 1rem;
  }
`;

interface MilestoneCardProps {
  status?: 'completed' | 'in-progress';
}

export const MilestoneCard = styled(motion.div)<MilestoneCardProps>`
  background: var(--card-bg);
  border: 1px solid var(--card-border);
  border-radius: 10px;
  padding: 1.5rem;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
  cursor: pointer;
  height: 450px;
  display: flex;
  flex-direction: column;

  @media (max-width: 768px) {
    padding: 1rem;
    height: 400px;
  }

  &:hover {
    border-color: var(--accent-color);
    box-shadow: 0 0 20px var(--card-shadow);
  }

  h3 {
    color: var(--accent-color);
    margin-bottom: 1rem;
    font-size: 1.2rem;
    display: flex;
    align-items: center;
    gap: 0.5rem;
    cursor: pointer;
    flex-shrink: 0;

    &:hover {
      opacity: 0.8;
    }

    svg {
      transition: transform 0.3s ease;
    }

    &:hover svg {
      transform: scale(1.1);
    }
  }

  .cert-container {
    flex: 1;
    overflow-y: auto;
    padding-right: 0.5rem;
    margin-right: -0.5rem;
    scrollbar-width: thin;
    scrollbar-color: var(--accent-color) var(--bg-secondary);

    &::-webkit-scrollbar {
      width: 6px;
    }

    &::-webkit-scrollbar-track {
      background: var(--bg-secondary);
      border-radius: 3px;
    }

    &::-webkit-scrollbar-thumb {
      background: var(--accent-color);
      border-radius: 3px;
    }
  }

  .cert-item {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    padding: 0.5rem;
    border-radius: 6px;
    transition: all 0.2s ease;
    cursor: pointer;
    margin-bottom: 0.5rem;
    position: relative;

    &:last-child {
      margin-bottom: 0;
    }

    &:hover {
      background: var(--bg-secondary);
      transform: translateX(4px);
    }

    svg {
      color: var(--accent-color);
      font-size: 1.2rem;
      flex-shrink: 0;
    }

    .cert-info {
      flex: 1;
      min-width: 0;
    }

    .cert-name {
      color: var(--text-primary);
      font-size: 0.9rem;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    /* Add click indicator only for completed certificates */
    ${props => props.status === 'completed' && `
      &::after {
        content: 'Click to view';
        position: absolute;
        right: 0.5rem;
        font-size: 0.7rem;
        color: var(--accent-color);
        opacity: 0;
        transition: opacity 0.2s ease;
      }

      &:hover::after {
        opacity: 1;
      }
    `}
  }
`;

export const AdditionalProjects = styled.div`
  margin-top: 1.5rem;
  padding: 1rem;
  background: var(--card-bg);
  border: 1px solid var(--card-border);
  border-radius: 10px;
  width: 100%;

  .header {
    display: flex;
    align-items: center;
    margin-bottom: 0.75rem;

    h3 {
      color: var(--accent-color);
      font-size: 1rem;
      font-weight: 600;
      margin: 0;
      white-space: nowrap;
    }

    .line {
      flex: 1;
      height: 1px;
      background: var(--accent-color);
      opacity: 0.3;
      margin-left: 0.75rem;
    }
  }

  .projects-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
    gap: 0.5rem;
    padding: 0.25rem;
  }

  .project-item {
    padding: 0.5rem;
    background: var(--bg-secondary);
    border: 1px solid var(--card-border);
    border-radius: 6px;
    transition: all 0.2s ease;
    cursor: pointer;

    &:hover {
      transform: translateY(-1px);
      border-color: var(--accent-color);
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
    }

    .project-name {
      font-size: 0.85rem;
      font-weight: 500;
      color: var(--text-primary);
      line-height: 1.3;
      overflow: hidden;
      text-overflow: ellipsis;
      display: -webkit-box;
      -webkit-line-clamp: 2;
      -webkit-box-orient: vertical;
    }
  }
`;